"use client"

import type { ComponentType, FunctionComponent } from "react"

import { clsx } from "clsx"
import { Fragment } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { ChevronLeft } from "@jobteaser/spark/components/Icons/ChevronLeft"
import { ChevronRight } from "@jobteaser/spark/components/Icons/ChevronRight"
import { Text } from "@jobteaser/spark/components/Text"

import { Link } from "@/modules/routing/components/Link"

import { ELLIPSIS, ELLIPSIS_AFTER, ELLIPSIS_BEFORE, generateRange } from "./helpers/generateRange"

import styles from "./Pagination.module.css"

const DEFAULT_QUERY_PARAMETER_NAME = "page"

type PaginationProps = {
  ariaLabelNextButton: string
  ariaLabelPageButton: string
  ariaLabelPreviousButton: string
  className?: string
  countPerPage: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkComponent?: ComponentType<any>
  onPaginate?: () => void
  pathname: string
  queryParameterName?: string
  scrollToTopOnPaginate?: ScrollBehavior | undefined
  searchParams: URLSearchParams | string
  siblingsCount?: number
  totalCount: number
  useNextLink?: boolean
}

function getPaginatedUrl({
  page,
  pathname,
  queryParameterName,
  searchParams,
}: {
  page: number
  pathname: string
  queryParameterName: string
  searchParams: URLSearchParams
}): string {
  const newSearchParams = new URLSearchParams(searchParams)

  newSearchParams.set(queryParameterName, `${page}`)

  return `${pathname}?${newSearchParams.toString()}`
}

export const Pagination: FunctionComponent<PaginationProps> = ({
  ariaLabelPageButton,
  ariaLabelPreviousButton,
  ariaLabelNextButton,
  className,
  countPerPage,
  linkComponent = "a",
  onPaginate,
  pathname,
  queryParameterName = DEFAULT_QUERY_PARAMETER_NAME,
  scrollToTopOnPaginate,
  searchParams,
  siblingsCount = 0,
  totalCount,
  useNextLink,
  ...props
}) => {
  if (totalCount < countPerPage) {
    return null
  }

  const newSearchParams = new URLSearchParams(searchParams)
  const lastPage = Math.ceil(totalCount / countPerPage)
  const currentPage = newSearchParams.has(queryParameterName) ? Number(newSearchParams.get(queryParameterName)) || 1 : 1
  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === lastPage
  const range = generateRange({
    currentPage,
    lastPage,
    siblingsCount,
  })
  const hasBeforeEllipsisInRange = range.includes(ELLIPSIS_BEFORE)
  const hasAfterEllipsisInRange = range.includes(ELLIPSIS_AFTER)
  const as = useNextLink ? Link : linkComponent

  const handleClick = (): void => {
    if (scrollToTopOnPaginate) {
      setTimeout(() => {
        document.documentElement.scrollIntoView({ behavior: scrollToTopOnPaginate })
      }, 50)
    }

    onPaginate?.()
  }

  return (
    <nav {...props} role="navigation" className={clsx(className, styles.main)}>
      <Button
        {...(isFirstPage
          ? { disabled: true }
          : {
              href: getPaginatedUrl({
                page: currentPage - 1,
                pathname,
                queryParameterName,
                searchParams: newSearchParams,
              }),
              onClick: handleClick,
              renderComponent: as,
            })}
        aria-label={ariaLabelPreviousButton}
        className={clsx(styles.item, styles.item___next)}
        size="small"
        variant="tertiary"
      >
        <ChevronLeft size={20} />
      </Button>
      {range.map((item, idx) => {
        const key = `${item}_${idx}`

        if (item === ELLIPSIS_BEFORE || item === ELLIPSIS_AFTER) {
          return (
            <Text
              aria-hidden
              as="span"
              className={clsx(styles.item, styles.item___ellipsis, styles.item___current_sibling)}
              key={key}
              variant="body1"
            >
              {ELLIPSIS}
            </Text>
          )
        }

        return (
          <Fragment key={key}>
            {/* We had an ellipsis right before the last page link for mobile purpose */}
            {idx === range.length - 1 && !hasAfterEllipsisInRange && currentPage < lastPage - 1 && (
              <Text
                aria-hidden
                as="span"
                className={clsx(
                  styles.item,
                  styles.item___ellipsis,
                  styles.item___mobile_ellipsis,
                  styles.item___current_sibling
                )}
                key={`${key}_${ELLIPSIS_AFTER}`}
                variant="body1"
              >
                {ELLIPSIS}
              </Text>
            )}

            <Button
              {...(as !== "button"
                ? {
                    href: getPaginatedUrl({
                      page: item,
                      pathname,
                      queryParameterName,
                      searchParams: newSearchParams,
                    }),
                  }
                : {})}
              aria-label={ariaLabelPageButton.replace("%{page}", `${item}`)}
              renderComponent={as}
              className={clsx(styles.item, {
                [styles.item___first]: item === 1,
                [styles.item___last]: item === lastPage,
                [styles.item___current]: currentPage === item,
                [styles.item___current_sibling]: ![1, currentPage, lastPage].includes(item),
                [styles.item___current_close_sibling]: item === currentPage - 1 || item === currentPage + 1,
              })}
              key={key}
              onClick={handleClick}
              size="small"
              variant={currentPage === item ? "highlight" : "tertiary"}
            >
              {item}
            </Button>

            {/* We had an ellipsis right after the first page link for mobile purpose */}
            {idx === 0 && !hasBeforeEllipsisInRange && currentPage > 2 && (
              <Text
                aria-hidden
                as="span"
                className={clsx(
                  styles.item,
                  styles.item___ellipsis,
                  styles.item___mobile_ellipsis,
                  styles.item___current_sibling
                )}
                key={`${key}_${ELLIPSIS_BEFORE}`}
                variant="body1"
              >
                {ELLIPSIS}
              </Text>
            )}
          </Fragment>
        )
      })}
      <Button
        {...(isLastPage
          ? { disabled: true }
          : {
              href: getPaginatedUrl({
                page: currentPage + 1,
                pathname,
                queryParameterName,
                searchParams: newSearchParams,
              }),
              onClick: handleClick,
              renderComponent: as,
            })}
        aria-label={ariaLabelNextButton}
        className={clsx(styles.item, styles.item___previous)}
        size="small"
        variant="tertiary"
      >
        <ChevronRight size={20} />
      </Button>
    </nav>
  )
}
